import { useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useTranslation } from 'react-i18next'

// local imports - types
import calendarLoading from 'assets/icons/loading-calendar.png'
import DateUtil from 'utils/DateUtil'
import Loader from 'components/ui/Loader'
import { IFilteredMeetingRoomList } from 'types/MeetingRoomType'
import useGetMeetingRoomResTable from '../hooks/use-get-meeting-room-res-table'
import Scheduler from './Scheduler'
import ScheduleSidebar from './ScheduleSidebar'
import ReservationModalHeader from './ReservationModalHeader'
import WeekSlider from 'components/home/on-campus-services/health-service/appointment-scheduler/WeekSlider'
import { ActionArea, CancelButton, ConfirmButton } from '../../gym/gym-list/styled'

/* Types */
interface IModalFooterProps {
  disabled: boolean
  onCancel: () => void
  onSave: () => void
}

interface IAppointmentModalProps {
  filterDate: Date
  selectedRoom: IFilteredMeetingRoomList
  open: boolean
  closeModal: (reset?: boolean) => void
  openModal: () => void
  showNotification: () => void
}

export default function ReservationModalWrapper({
  filterDate: dateFromList,
  selectedRoom,
  open,
  closeModal,
  openModal,
  showNotification,
}: IAppointmentModalProps) {
  const { t } = useTranslation()

  /* Meeting Room - reservationDate filter value for appointment date filtering etc. */
  const [filterDate, setFilterDate] = useState<Date>(dateFromList)
  const lastFilteredDate = DateUtil.getWeekStartEndFromDate(dateFromList)
  useEffect(() => setFilterDate(dateFromList), [dateFromList])

  /* GET Selected Meeting Room's Reservation Table List | Custom Hook  */
  const locationId = selectedRoom.floor.locationId
  const meetingRoomId = selectedRoom.id
  const {
    availableDates,
    tableWeek,
    setTableWeek,
    selectedReservations,
    setSelectedReservations,
    sidebarVisible,
    setSidebarVisible,
    loading,
    dailyAppointments,
  } = useGetMeetingRoomResTable(locationId, meetingRoomId, dateFromList)

  /* Open Appointment Sidebar */
  const openAppointmentSidebar = () => {
    setSidebarVisible(true)
    closeModal()
  }

  /* Close Sidebar and reset states */
  const closeSidebarAfterSave = () => {
    setSidebarVisible(false)
    setSelectedReservations(undefined)
    showNotification()
    setFilterDate(dateFromList)
    setTableWeek(lastFilteredDate)
  }

  /* Close with cancel */
  const handleCloseOnCancel = () => {
    closeModal(true)
    setFilterDate(dateFromList)
    setSelectedReservations(undefined)
    setTableWeek(lastFilteredDate)
  }

  return (
    <>
      <Dialog
        id="appointmentModal"
        blockScroll
        keepInViewport
        focusOnShow={false}
        closeOnEscape={false}
        closable={true}
        header={<span>{`${t('meeting-res-modal.meeting-reservation')}`}</span>}
        draggable={false}
        resizable={false}
        visible={open}
        style={{ width: '95vw', height: '90vh' }}
        breakpoints={{ '1536px': '95vw', '1280': '95vw' }}
        onHide={handleCloseOnCancel}
        footer={
          <FooterComponent
            onSave={openAppointmentSidebar}
            onCancel={handleCloseOnCancel}
            disabled={!selectedReservations || !selectedReservations?.length}
          />
        }>
        {/* Modal Header */}
        <ReservationModalHeader
          filterDate={filterDate}
          availableDateRange={availableDates}
          setFilterDate={setFilterDate}
          room={selectedRoom}
          setTableWeek={setTableWeek}
        />

        {/* Week Change Slider */}
        <WeekSlider availableDateRange={availableDates} tableWeek={tableWeek} setTableWeek={setTableWeek} />

        {/* Content - Calendar View */}
        {loading ? (
          <Loader src={calendarLoading} />
        ) : (
          <Scheduler
            filterDate={filterDate}
            setFilterDate={setFilterDate}
            tableWeek={tableWeek}
            dailyAppointments={dailyAppointments}
            selectedReservations={selectedReservations}
            setSelectedReservations={setSelectedReservations}
          />
        )}
      </Dialog>

      {/* Schedule - Save Appointment Sidebar */}
      {!!selectedReservations && selectedReservations?.length > 0 && (
        <ScheduleSidebar
          openModal={openModal}
          visible={sidebarVisible}
          setVisible={setSidebarVisible}
          closeAfterSave={closeSidebarAfterSave}
          roomInfo={selectedRoom}
          reservationInfos={selectedReservations}
        />
      )}
    </>
  )
}

function FooterComponent({ onSave, onCancel, disabled }: IModalFooterProps) {
  const { t } = useTranslation()
  return (
    <ActionArea>
      <CancelButton id="closeModal" className="p-button-outlined" label={t('buttons.cancel')} onClick={onCancel} />
      <ConfirmButton id="openSidebar" disabled={disabled} label={t('buttons.book-reservation')} onClick={onSave} />
    </ActionArea>
  )
}
